/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type {
  ConditionFieldDescription,
  ConditionsConfig_ConditionConfig,
  ConditionsConfig_ConditionsConfigGroup,
} from "@smartsuite/automation-engine-api";
import { ValueDescription_T } from "@smartsuite/automation-engine-api";
import type { TriggerMerger } from ".";
import { getDefaultFunction } from "../../../../containers/AutomationForm/Sections/TriggerSection/ConditionsForm/ConditionItem/utils/default-function";
import {
  getAvailableFunctions,
  getNestedFnConfig,
} from "../../../../containers/AutomationForm/Sections/TriggerSection/ConditionsForm/ConditionItem/utils/function-utils";
import type { TriggerCandidate } from "../../../../types/candidate";

export const mergeDefaultCondition =
  (): TriggerMerger => (trigger: TriggerCandidate) => {
    let newCondition: ConditionsConfig_ConditionsConfigGroup;
    if (trigger.conditions?.group.length === 0) {
      newCondition = getDefaultConditionsGroup(trigger);
    } else {
      newCondition = {
        group: {
          oneofKind: "condition",
          condition: {
            conditionFieldId: "",
          },
        },
      };
    }
    return {
      ...trigger,
      conditions: {
        ...trigger.conditions!,
        group: [...(trigger.conditions?.group ?? []), newCondition],
      },
    };
  };

export const mergeInitialCondition =
  (): TriggerMerger => (trigger: TriggerCandidate) => {
    return {
      ...trigger,
      conditions: {
        operator: trigger.conditions!.operator,
        group: [getDefaultConditionsGroup(trigger)],
      },
    };
  };

export const getDefaultConditionsGroup = (
  trigger: TriggerCandidate,
): ConditionsConfig_ConditionsConfigGroup => ({
  group: {
    oneofKind: "condition",
    condition: getDefaultCondition(trigger),
  },
});

const getDefaultCondition = (
  trigger: TriggerCandidate,
): ConditionsConfig_ConditionConfig => {
  let conditionConfig: ConditionsConfig_ConditionConfig = {
    conditionFieldId: "field-title",
    function: {
      function: {
        oneofKind: "contains",
        contains: { right: { values: [] } },
      },
    },
  };
  let condition: ConditionFieldDescription | undefined;
  if (trigger.triggerReference?.triggerId !== undefined) {
    condition = trigger.description.conditionFields.find(
      (description) =>
        description.value?.type === ValueDescription_T.SELECT &&
        description.conditionFieldId === "field-status",
    );
    if (condition === undefined) {
      condition = trigger.description.conditionFields.find(
        (description) =>
          description.value?.type === ValueDescription_T.SELECT &&
          !description.value?.multiple,
      );
    }
    if (condition === undefined) {
      condition = trigger.description.conditionFields.find(
        (description) =>
          description.value?.type === ValueDescription_T.SELECT &&
          description.value?.multiple,
      );
    }

    if (condition === undefined) {
      condition = trigger.description.conditionFields.find(
        (description) =>
          description.value?.type === ValueDescription_T.DATE &&
          description.conditionFieldId === "field-due_date",
      );
    }

    if (condition?.editor?.editor.oneofKind !== undefined) {
      const defaultCondition = getDefaultFunction(
        getAvailableFunctions(getNestedFnConfig(trigger, true), condition),
        condition,
        trigger.triggerReference?.triggerId,
      );
      if (defaultCondition !== undefined) {
        conditionConfig = {
          conditionFieldId: condition.conditionFieldId,
          function: defaultCondition,
        };
      }
    }
  }

  return conditionConfig;
};

export const mergeUpdatedCondition =
  (condition: ConditionsConfig_ConditionConfig, index: number): TriggerMerger =>
  (trigger: TriggerCandidate) => ({
    ...trigger,
    conditions: {
      ...trigger.conditions!,
      group: trigger.conditions!.group.map((item, i) =>
        i === index
          ? {
              group: {
                oneofKind: "condition",
                condition,
              },
            }
          : item,
      ),
    },
  });

export const ejectCondition =
  (index: number): TriggerMerger =>
  (trigger: TriggerCandidate) => ({
    ...trigger,
    conditions: {
      ...trigger.conditions!,
      group: trigger.conditions!.group.filter((_, i) => i !== index),
    },
  });
